//>>> Domain detection utility for owner/tenant API routing
/**
 * Detects the current domain type based on window.location
 * Owner domains: admin.freightezcrm.com, owner.freightezcrm.com, etc.
 * Tenant domains: *.freightezcrm.com (subdomains), custom domains
 *
 * @returns {string} 'owner' | 'tenant'
 */
export const detectDomain = () => {
    if (typeof window === 'undefined') {
        // SSR fallback - default to tenant
        return 'tenant';
    }

    const hostname = window.location.hostname;

    // Dev override: set localStorage __domain_override to "owner" | "tenant"
    const explicitDomain = localStorage.getItem('__domain_override');
    if (explicitDomain === 'owner' || explicitDomain === 'tenant') {
        return explicitDomain;
    }

    // Do not match localhost here — otherwise ENV.API_URL uses VITE_OWNER_API_URL and ignores VITE_TENANT_API_URL.
    const ownerPatterns = [/^admin\./i, /^owner\./i, /^manage\./i];

    if (ownerPatterns.some((pattern) => pattern.test(hostname))) {
        return 'owner';
    }

    // Default to tenant (subdomain or custom domain)
    return 'tenant';
};

/**
 * Gets the current domain type
 * @returns {string} 'owner' | 'tenant'
 */
export const getDomain = () => {
    return detectDomain();
};

/**
 * Checks if current domain is owner
 * @returns {boolean}
 */
export const isOwnerDomain = () => {
    return detectDomain() === 'owner';
};

/**
 * Checks if current domain is tenant
 * @returns {boolean}
 */
export const isTenantDomain = () => {
    return detectDomain() === 'tenant';
};

/**
 * Sets domain override for development/testing
 * @param {string} domain - 'owner' | 'tenant'
 */
export const setDomainOverride = (domain) => {
    if (domain === 'owner' || domain === 'tenant') {
        localStorage.setItem('__domain_override', domain);
    } else {
        localStorage.removeItem('__domain_override');
    }
};
